import { KeyRound } from 'lucide-react';
import React from 'react';
import { StatusChip } from '../components/Avatar';
import { EmptyState } from '../components/EmptyState';
import { SectionCard } from '../components/SectionCard';
import { useFeedback } from '../contexts/FeedbackContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useSession } from '../contexts/SessionContext';
import { deleteTenantApiKey } from '../api';
import { formatAbsoluteDate, formatRelativeDate } from '../utils/formatters';

function ReleaseAccessPage() {
  const { session } = useSession();
  const { workspace, setWorkspaceVersion } = useWorkspace();
  const { confirm, toast } = useFeedback();
  const { apiKeys } = workspace;

  const activeApiKeys = apiKeys.filter((k) => k.isActive);
  const sortedApiKeys = [...apiKeys].sort((a, b) => {
    if (a.isActive !== b.isActive) return Number(b.isActive) - Number(a.isActive);
    return (b.created || '').localeCompare(a.created || '');
  });
  const expiringSoon = activeApiKeys.filter((key) => {
    if (!key.expiresAt) return false;
    const remaining = new Date(key.expiresAt).getTime() - Date.now();
    return remaining > 0 && remaining <= 30 * 86_400_000;
  });
  const unrestrictedKeys = activeApiKeys.filter((key) => key.scopes.includes('*'));

  async function handleDeleteApiKey(tenantApiKeyId: string, keyName: string) {
    if (!session) return;
    const confirmed = await confirm({
      title: 'Revoke API key?',
      message: `"${keyName}" will stop working immediately and any services using it will lose access. This cannot be undone.`,
      confirmLabel: 'Revoke key',
      danger: true
    });
    if (!confirmed) return;
    try {
      await deleteTenantApiKey(session.accessToken, tenantApiKeyId);
      toast('success', `API key "${keyName}" revoked`);
      setWorkspaceVersion((value) => value + 1);
    } catch (error: unknown) {
      toast('error', error instanceof Error ? error.message : 'Failed to revoke API key.');
    }
  }

  return (
    <div className="apiKeyWorkspace">
      <div className="workspaceAccessSummary" aria-label="API key summary">
        <span><strong>{activeApiKeys.length}</strong> active</span>
        <span className={unrestrictedKeys.length > 0 ? 'is-warning' : undefined}><strong>{unrestrictedKeys.length}</strong> full access</span>
        <span><strong>{expiringSoon.length}</strong> expiring within 30 days</span>
        <span className="workspaceAccessRole">Last created: <strong>{sortedApiKeys[0] ? formatRelativeDate(sortedApiKeys[0].created) : 'never'}</strong></span>
      </div>
      <SectionCard
        className="apiKeysPanel"
        label="Auth"
        title="API keys"
        description="Scoped API keys for CI/CD pipelines, SDK integrations, and Cindro CLI access."
      >
        {apiKeys.length > 0 ? (
          <div className="releaseTableWrap" role="region" aria-label="API keys table" tabIndex={0}>
            <table className="releaseTable apiKeyTable">
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Access</th>
                  <th>Expires</th>
                  <th>Created</th>
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {sortedApiKeys.map((apiKey) => (
                  <tr key={apiKey.tenantApiKeyId}>
                    <td>
                      <div className="releaseIdentity apiKeyIdentity">
                        <strong>{apiKey.name}</strong>
                        <code>{apiKey.prefix ? `${apiKey.prefix}••••••••` : '••••••••••••'}</code>
                      </div>
                    </td>
                    <td>
                      <div className="apiKeyScopes">
                        {apiKey.scopes.includes('*') ? (
                          <span className="scopeBadge scopeBadge--wildcard">Full access</span>
                        ) : apiKey.scopes.map((scope) => <span key={scope} className="scopeBadge">{scope}</span>)}
                      </div>
                    </td>
                    <td>
                      <div className="releaseIdentity">
                        <strong>{apiKey.expiresAt ? formatRelativeDate(apiKey.expiresAt) : 'Never'}</strong>
                        <span>{apiKey.expiresAt ? formatAbsoluteDate(apiKey.expiresAt) : 'No automatic expiry'}</span>
                      </div>
                    </td>
                    <td>
                      <div className="releaseIdentity">
                        <strong>{formatRelativeDate(apiKey.created)}</strong>
                        <span>{formatAbsoluteDate(apiKey.created)}</span>
                      </div>
                    </td>
                    <td>
                      <StatusChip tone={apiKey.isActive ? 'success' : 'neutral'}>
                        {apiKey.isActive ? 'Active' : 'Revoked'}
                      </StatusChip>
                    </td>
                    <td className="releaseTableAction">
                      {apiKey.isActive ? (
                        <button
                          type="button"
                          className="dangerButton"
                          onClick={() => handleDeleteApiKey(apiKey.tenantApiKeyId, apiKey.name)}
                        >
                          Revoke
                        </button>
                      ) : <span className="recoveryUnavailable">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={<KeyRound size={20} />}
            title="No API keys yet"
            description="Create a key to authenticate your CI/CD pipeline or Cindro SDK. Keys are shown once on creation."
          />
        )}
      </SectionCard>
    </div>
  );
}

export { ReleaseAccessPage };
